import React, { useState, useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { customerAPI, hiresAPI } from '../services/api.ts';
import {
  ClipboardDocumentListIcon,
  MapPinIcon,
  UsersIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface CustomerStats {
  totalSites: number;
  totalHires: number;
  activeHires: number;
  overdueHires: number;
  totalUsers: number;
  totalSpending: number;
}

interface OverdueHire {
  id: string;
  hire_reference: string;
  target_return_date: string;
  cost_to_date: number;
  equipment_name: string;
  supplier_name: string;
  site_name: string;
}

interface EndingSoonHire {
  id: string;
  hire_end_date: string;
  hire_status: string;
  equipment_name: string;
  equipment_image_url?: string;
  supplier_name: string;
  site_name: string;
  site_address: string;
  urgency_status: 'overdue' | 'ending_soon' | 'active';
}

export const Dashboard: React.FC = () => {
  const { user, customer, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [overdueHires, setOverdueHires] = useState<OverdueHire[]>([]);
  const [endingSoonHires, setEndingSoonHires] = useState<EndingSoonHire[]>([]);
  const [isLoading, setIsLoading] = useState(true);



  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log('Fetching dashboard data...');
        console.log('User:', user);
        console.log('Customer:', customer);
        
        // Check for token in URL parameters (from OAuth redirects)
        const tokenFromUrl = searchParams.get('token');
        if (tokenFromUrl) {
          console.log('Token found in URL, storing in localStorage');
          localStorage.setItem('token', tokenFromUrl);
          // Remove token from URL to clean up
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('token');
          window.history.replaceState({}, '', newUrl.toString());
          // Refresh the auth context with the new token
          await refreshProfile();
        }
        
        const token = localStorage.getItem('token');
        console.log('Token in localStorage:', token);
        console.log('Token length:', token?.length);
        console.log('Token starts with:', token?.substring(0, 20));

        // Get stats first (this works)
        const statsResponse = await customerAPI.getStats();
        console.log('Stats response:', statsResponse);
        setStats(statsResponse.data.stats);

        // Get hires ending soon (Plant Reminders)
        const endingSoonResponse = await hiresAPI.getEndingSoon();
        console.log('Ending soon response:', endingSoonResponse);
        setEndingSoonHires(endingSoonResponse.data.endingSoon);

        // Overdue hires call removed - was causing 500 errors
        setOverdueHires([]);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        console.error('Error config:', error.config);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, customer, refreshProfile, searchParams]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'new-hire':
        navigate('/app/hires?open_modal=true');
        break;
      case 'add-site':
        navigate('/app/sites?open_modal=true');
        break;
      case 'manage-users':
        navigate('/app/users');
        break;
      case 'view-reports':
        // For now, navigate to hires page - we can add a reports page later
        navigate('/app/hires');
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="card">
        <div className="card-body">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.name}!
            </h1>
            <p className="text-gray-600 mt-1">
              Here's what's happening with your equipment management.
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Account Status</div>
            <span className={`inline-flex items-center  ${
              customer?.status === 'free' 
                ? 'badge badge-error'
                : customer?.status === 'active'
                ? 'badge badge-success'
                : 'badge badge-warning'
            }`}>
              {customer?.status === 'free' ? 'Free Edition' : customer?.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentListIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Hires
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.totalHires || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-success-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Hires
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.activeHires || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapPinIcon className="h-8 w-8 text-secondary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Sites
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.totalSites || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyPoundIcon className="h-8 w-8 text-warning-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Spending
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(stats?.totalSpending || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overdue Hires Alert */}
      {overdueHires.length > 0 && (
        <div className="card border-l-4 border-danger-400">
          <div className="card-body">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-danger-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-800">
                  Overdue Equipment Returns
                </h3>
                <div className="mt-2 text-sm text-danger-700">
                  <p>You have {overdueHires.length} equipment hire{overdueHires.length !== 1 ? 's' : ''} that {overdueHires.length === 1 ? 'is' : 'are'} overdue for return.</p>
                </div>
                <div className="mt-4">
                  <div className="space-y-2">
                    {overdueHires.slice(0, 3).map((hire) => (
                      <div key={hire.id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{hire.equipment_name}</span>
                          <span className="text-gray-500 ml-2">from {hire.supplier_name}</span>
                          {hire.site_name && (
                            <span className="text-gray-500 ml-2">at {hire.site_name}</span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-danger-600 font-medium">
                            Due {formatDate(hire.target_return_date)}
                          </div>
                          <div className="text-gray-500">
                            {formatCurrency(hire.cost_to_date)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {overdueHires.length > 3 && (
                      <div className="text-sm text-gray-500">
                        ... and {overdueHires.length - 3} more
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <button 
                      onClick={() => navigate('/app/hires')}
                      className="text-sm text-danger-600 hover:text-danger-800 font-medium"
                    >
                      View all hires →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <button 
              onClick={() => handleQuickAction('new-hire')}
              className="btn btn-primary"
            >
              <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
              New Hire
            </button>
            <button 
              onClick={() => handleQuickAction('add-site')}
              className="btn btn-outline"
            >
              <MapPinIcon className="h-5 w-5 mr-2" />
              Add Site
            </button>
            <button 
              onClick={() => handleQuickAction('manage-users')}
              className="btn btn-outline"
            >
              <UsersIcon className="h-5 w-5 mr-2" />
              Manage Users
            </button>
            <button 
              onClick={() => handleQuickAction('view-reports')}
              className="btn btn-outline"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              View Reports
            </button>
          </div>
        </div>
      </div>

      {/* Plant Reminders */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Active Equipment Hires</h3>
          <p className="text-sm text-gray-600">All active equipment hires requiring attention</p>
        </div>
        <div className="card-body">
          {endingSoonHires.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600">No active equipment hires</p>
            </div>
          ) : (
            <div className="space-y-4">
              {endingSoonHires.map((hire) => {
                const isOverdue = hire.urgency_status === 'overdue';
                const isEndingSoon = hire.urgency_status === 'ending_soon';
                
                return (
                  <div key={hire.id} className={`flex items-center p-4 rounded-lg border ${
                    isOverdue 
                      ? 'bg-red-50 border-red-200' 
                      : isEndingSoon 
                        ? 'bg-orange-50 border-orange-200' 
                        : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex-shrink-0 mr-4">
                      {hire.equipment_image_url ? (
                        <img 
                          src={hire.equipment_image_url} 
                          alt={hire.equipment_name}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                          <ClipboardDocumentListIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${
                            isOverdue 
                              ? 'text-red-600' 
                              : isEndingSoon 
                                ? 'text-orange-600' 
                                : 'text-blue-600'
                          }`}>
                            {hire.equipment_name}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {hire.site_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {hire.supplier_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${
                            isOverdue 
                              ? 'text-red-600' 
                              : isEndingSoon 
                                ? 'text-orange-600' 
                                : 'text-blue-600'
                          }`}>
                            {isOverdue ? 'Overdue' : isEndingSoon ? 'Ends Soon' : 'Active'} - {formatDate(hire.hire_end_date)}
                          </p>
                          <button 
                            onClick={() => navigate(`/app/hires?edit=${hire.id}`)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Edit Hire →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="mt-4">
                <button 
                  onClick={() => navigate('/app/hires')}
                  className="btn btn-secondary"
                >
                  View all hires →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
